import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BasicTableOne from "../../components/tables/BasicTableOne";
import { fetchOpenTasks, fetchMyTasks, fetchErrorTasks, fetchClosedKOTasks, fetchDiscartedTasks } from "../../apis/apis";
import { useAuth } from "../../context/AuthContext";

export default function TaskInbox() {
  const { user } = useAuth(); // Obtener el usuario desde AuthContext

  type Task = {
    task_id: string;
    name: string;
    description: string;
    subject_name: string;
    university_name: string;
    degree_name: string;
    state?: string;
    workflow_ref?: string;
    eval_type?: string;
  };

  const [openTasks, setOpenTasks] = useState<Task[]>([]); // Definir tipo explícito
  const [myTasks, setMyTasks] = useState<Task[]>([]); // Definir tipo explícito
  const [errorTasks, setErrorTasks] = useState<Task[]>([]); // Definir tipo explícito
  const [incompletedTasks, setIncompletedTasks] = useState<Task[]>([]); // Definir tipo explícito
  const [discardedTasks, setDiscardedTasks] = useState<Task[]>([]);

  const [loadingOpenTasks, setLoadingOpenTasks] = useState(true);
  const [loadingMyTasks, setLoadingMyTasks] = useState(true);
  const [loadingErrorTasks, setLoadingErrorTasks] = useState(true);
  const [loadingIncompletedTasks, setLoadingIncompletedTasks] = useState(true);
  const [loadingDiscardedTasks, setLoadingDiscardedTasks] = useState(true);

  const [errorOpenTasks, setErrorOpenTasks] = useState<string | null>(null);
  const [errorMyTasks, setErrorMyTasks] = useState<string | null>(null);
  const [errorErrorTasks, setErrorErrorTasks] = useState<string | null>(null);
  const [errorIncompletedTasks, setErrorIncompletedTasks] = useState<string | null>(null);
  const [errorDiscardedTasks, setErrorDiscardedTasks] = useState<string | null>(null);

  useEffect(() => {
    const loadOpenTasks = async () => {
      try {
        setLoadingOpenTasks(true);
        const tasks = await fetchOpenTasks();
        setOpenTasks(tasks);
      } catch (err) {
        console.error("Error fetching open tasks:", err);
        setErrorOpenTasks("Failed to load open tasks. Please try again later.");
      } finally {
        setLoadingOpenTasks(false);
      }
    };

    const loadMyTasks = async () => {
      try {
        setLoadingMyTasks(true);
        const tasks = await fetchMyTasks();
        setMyTasks(tasks);
      } catch (err) {
        console.error("Error fetching my tasks:", err);
        setErrorMyTasks("Failed to load my tasks. Please try again later.");
      } finally {
        setLoadingMyTasks(false);
      }
    };

    const loadErrorTasks = async () => {
      try {
        setLoadingErrorTasks(true);
        const tasks = await fetchErrorTasks();
        setErrorTasks(tasks);
      } catch (err) {
        console.error("Error fetching error tasks:", err);
        setErrorErrorTasks("Failed to load error tasks. Please try again later.");
      } finally {
        setLoadingErrorTasks(false);
      }
    };

    const loadIncompletedTasks = async () => {
      try {
        setLoadingIncompletedTasks(true);
        const tasks = await fetchClosedKOTasks();
        setIncompletedTasks(tasks);
      } catch (err) {
        console.error("Error fetching incompleted tasks:", err);
        setErrorIncompletedTasks("Failed to load incompleted tasks. Please try again later.");
      } finally {
        setLoadingIncompletedTasks(false);
      }
    };

    const loadDiscardedTasks = async () => {
      try {
        setLoadingDiscardedTasks(true);
        const tasks = await fetchDiscartedTasks();
        setDiscardedTasks(tasks);
      } catch (err) {
        console.error("Error fetching discarded tasks:", err);
        setErrorDiscardedTasks("Failed to load discarded tasks. Please try again later.");
      } finally {
        setLoadingDiscardedTasks(false);
      }
    };

    loadOpenTasks();
    loadMyTasks();

    if (user?.is_superuser) {
      loadErrorTasks();
      loadIncompletedTasks();
      loadDiscardedTasks();
    }
  }, [user]);

  return (
    <div className="p-4">
      {user?.is_superuser && (
        <>
          <h1 className="text-xl font-bold mt-8 mb-4">Task Error</h1>
          {loadingErrorTasks ? (
            <div className="p-4">Loading error tasks...</div>
          ) : errorErrorTasks ? (
            <div className="p-4 text-red-500">{errorErrorTasks}</div>
          ) : (
            <BasicTableOne
              data={errorTasks.map((task) => ({
                id: task.task_id,
                name: (
                  <Link
                    to={`/tasks/${task.task_id}`}
                    className="text-blue-500 hover:underline"
                  >
                    {task.name}
                  </Link>
                ),
                description: task.description,
                subject: task.subject_name,
                university: task.university_name,
                degree: task.degree_name,
                state: task.state,
                workflow_ref: task.workflow_ref, // Asegúrate de que este campo exista en tu API
                eval_type: task.eval_type, // Asegúrate de que este campo exista en tu API
              }))}
              columns={[
                { key: "name", label: "Task Name" },
                { key: "description", label: "Description" },
                { key: "subject", label: "Subject" },
                { key: "university", label: "University" },
                { key: "degree", label: "Degree" },
                { key: "state", label: "State" },
                { key: "workflow_ref", label: "workflow_ref" },
                { key: "eval_type", label: "Eval Type" },
              ]}
            />
          )}

          {/* Sección de tareas descartadas */}
          <h1 className="text-xl font-bold mt-8 mb-4">Task Discarded</h1>
          {loadingDiscardedTasks ? (
            <div className="p-4">Loading discarded tasks...</div>
          ) : errorDiscardedTasks ? (
            <div className="p-4 text-red-500">{errorDiscardedTasks}</div>
          ) : (
            <BasicTableOne
              data={discardedTasks.map((task) => ({
                id: task.task_id,
                name: (
                  <Link
                    to={`/tasks/${task.task_id}`}
                    className="text-blue-500 hover:underline"
                  >
                    {task.name}
                  </Link>
                ),
                description: task.description,
                subject: task.subject_name,
                university: task.university_name,
                degree: task.degree_name,
                state: task.state,
              }))}
              columns={[
                { key: "name", label: "Task Name" },
                { key: "description", label: "Description" },
                { key: "subject", label: "Subject" },
                { key: "university", label: "University" },
                { key: "degree", label: "Degree" },
                { key: "state", label: "State" },
              ]}
            />
          )}

          <h1 className="text-xl font-bold mt-8 mb-4">Task Incompleted</h1>
          {loadingIncompletedTasks ? (
            <div className="p-4">Loading incompleted tasks...</div>
          ) : errorIncompletedTasks ? (
            <div className="p-4 text-red-500">{errorIncompletedTasks}</div>
          ) : (
            <BasicTableOne
              data={incompletedTasks.map((task) => ({
                id: task.task_id,
                name: (
                  <Link
                    to={`/tasks/${task.task_id}`}
                    className="text-blue-500 hover:underline"
                  >
                    {task.name}
                  </Link>
                ),
                description: task.description,
                subject: task.subject_name,
                university: task.university_name,
                degree: task.degree_name,
                state: task.state,
              }))}
              columns={[
                { key: "name", label: "Task Name" },
                { key: "description", label: "Description" },
                { key: "subject", label: "Subject" },
                { key: "university", label: "University" },
                { key: "degree", label: "Degree" },
                { key: "state", label: "State" },
              ]}
            />
          )}
        </>
      )}

      <h1 className="text-xl font-bold mb-4">My Tasks</h1>
      {loadingMyTasks ? (
        <div className="p-4">Loading my tasks...</div>
      ) : errorMyTasks ? (
        <div className="p-4 text-red-500">{errorMyTasks}</div>
      ) : (
        <BasicTableOne
          data={myTasks.map((task) => ({
            id: task.task_id,
            name: (
              <Link
                to={`/tasks/${task.task_id}`}
                className="text-blue-500 hover:underline"
              >
                {task.name}
              </Link>
            ),
            description: task.description,
            subject: task.subject_name,
            university: task.university_name,
            degree: task.degree_name,
            state: task.state,
          }))}
          columns={[
            { key: "name", label: "Task Name" },
            { key: "description", label: "Description" },
            { key: "subject", label: "Subject" },
            { key: "university", label: "University" },
            { key: "degree", label: "Degree" },
            { key: "state", label: "State" },
          ]}
        />
      )}

      <h1 className="text-xl font-bold mt-8 mb-4">Task Inbox</h1>
      {loadingOpenTasks ? (
        <div className="p-4">Loading open tasks...</div>
      ) : errorOpenTasks ? (
        <div className="p-4 text-red-500">{errorOpenTasks}</div>
      ) : (
        <BasicTableOne
          data={openTasks.map((task) => ({
            id: task.task_id,
            name: (
              <Link
                to={`/tasks/${task.task_id}`}
                className="text-blue-500 hover:underline"
              >
                {task.name}
              </Link>
            ),
            description: task.description,
            subject: task.subject_name,
            university: task.university_name,
            degree: task.degree_name,
          }))}
          columns={[
            { key: "name", label: "Task Name" },
            { key: "description", label: "Description" },
            { key: "subject", label: "Subject" },
            { key: "university", label: "University" },
            { key: "degree", label: "Degree" },
          ]}
        />
      )}
    </div>
  );
}
